import React from "react";
import Pagination from "./Pagination";
import Skeleton from "./Skeleton";

type TableColumn = {
  key: string;
  header: React.ReactNode;
  align?: "left" | "center" | "right";
  accessor?: ({
    key,
    row,
    rowIndex,
  }: {
    key: string;
    row: any;
    rowIndex: number;
  }) => React.ReactNode;
  width?: string;
};

type TableData = Record<string, any>;

interface TableProps {
  columns?: Array<TableColumn>;
  data?: Array<TableData>;
  pagination?: {
    totalDocs: number;
    totalPages: number;
    currentPage: number;
    pageLimit: number;
    hasNextPage: number;
    hasPrevPage: number;
  } | null;
  className?: string;
  onPageChange?: (page: number) => void;
  /** Shows shimmering placeholder rows instead of the "No data" state — use
   * while the list is loading so the table never flashes empty first. */
  loading?: boolean;
  /** Number of shimmer rows to render while `loading` is true. */
  skeletonRows?: number;
  /** Optional per-row extra className, e.g. to flash-highlight a just-added row. */
  rowClassName?: (row: TableData, rowIndex: number) => string;
}

const TableV2: React.FC<TableProps> = ({
  columns = [],
  data = [],
  pagination = null,
  className = "",
  onPageChange,
  loading = false,
  skeletonRows = 6,
  rowClassName,
}) => {
  return (
    <div className={className}>
      <div className="border border-gray-100 rounded-xl overflow-hidden overflow-x-scroll dark:border-gray-700">
        <table className="w-full text-sm text-left text-gray-700 min-w-max dark:text-gray-200">
          {/* -------- Header -------- */}
          <thead className="bg-gray-50 text-gray-500 text-xs border-b border-gray-100 dark:bg-gray-900/60 dark:text-gray-400 dark:border-gray-700">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="py-3.5 px-4 font-medium whitespace-nowrap"
                  style={{ width: col?.width, textAlign: col?.align || "left" }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>

          {/* -------- Body -------- */}
          <tbody>
            {loading ? (
              Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                <tr
                  key={`skeleton-row-${rowIndex}`}
                  className="border-b border-gray-100 last:border-b-0 dark:border-gray-700"
                >
                  {columns.map((col, colIdx) => (
                    <td
                      key={`skeleton-col-${colIdx}`}
                      className="py-3 px-4 align-middle"
                      style={{ width: col?.width }}
                    >
                      <Skeleton className="h-4 w-full max-w-[180px] rounded-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : data && data.length > 0 ? (
              data.map((row, rowIndex) => (
                <tr
                  key={`row-${rowIndex}`}
                  className={`border-b border-gray-100 last:border-b-0 hover:bg-gray-50/70 transition-colors dark:border-gray-700 dark:hover:bg-gray-700/50 ${
                    rowClassName?.(row, rowIndex) || ""
                  }`}
                >
                  {columns.map((col, colIdx) => (
                    <td
                      key={`col-${colIdx}`}
                      className="py-3 px-4 align-middle"
                      style={{
                        width: col?.width,
                        textAlign: col?.align || "left",
                      }}
                    >
                      {col.accessor
                        ? col.accessor({ key: col.key, row, rowIndex })
                        : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr className="border-b border-gray-100 dark:border-gray-700">
                <td
                  colSpan={columns.length || 1}
                  className="py-6 px-4 text-center text-gray-500 dark:text-gray-400"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* -------- Pagination -------- */}
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          pageLimit={pagination.pageLimit}
          totalPages={pagination.totalPages}
          totalRecords={pagination.totalDocs}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

export default TableV2;
